import { AlertTriangle } from "lucide-react";
import type * as React from "react";
import { Alert, AlertDescription } from "@/components/ui/atoms/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/atoms/card";
import { cn } from "@/lib/utils";

export type ErrorStateProps = {
  /** Primary heading (default: "Something went wrong") */
  title?: string;
  /** Supporting text below the heading */
  description?: string;
  /** Error message shown in a destructive Alert */
  errorMessage?: string;
  /** Stack trace shown in development only */
  errorStack?: string;
  /** Force the developer details panel to render. Defaults to dev mode. */
  showDeveloperDetails?: boolean;
  /** Action buttons rendered in the card footer */
  children?: React.ReactNode;
} & Omit<React.ComponentProps<"div">, "children">;

export function ErrorState({
  title = "Something went wrong",
  description = "We've been notified of this error and will investigate.",
  errorMessage,
  errorStack,
  showDeveloperDetails = import.meta.env.DEV,
  children,
  className,
  ...props
}: ErrorStateProps) {
  const shouldShowDeveloperDetails = Boolean(
    errorStack && showDeveloperDetails
  );

  return (
    <div
      className={cn("flex h-full items-center justify-center p-4", className)}
      {...props}
    >
      <Card className="w-full max-w-md 2xl:max-w-xl" role="alert">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>

        {(errorMessage || errorStack) && (
          <CardContent className="space-y-4">
            {errorMessage && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="whitespace-pre-wrap break-words font-mono text-sm">
                  {errorMessage}
                </AlertDescription>
              </Alert>
            )}
            {shouldShowDeveloperDetails && (
              <details className="text-muted-foreground text-xs">
                <summary className="cursor-pointer hover:text-foreground">
                  Developer Details
                </summary>
                <pre className="mt-2 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                  {errorStack}
                </pre>
              </details>
            )}
          </CardContent>
        )}

        {children && <CardFooter className="flex gap-2">{children}</CardFooter>}
      </Card>
    </div>
  );
}
